import { useEffect, useRef, useState } from 'react';
import { nomDuChantier } from '../../lib/nom-chantier';
import { isoVersDate } from '../../api/mappers';
import { adresseApi } from '../../api/client.js';
import { bonsAchatApi, depensesApi, piecesApi } from '../../api/resources';
import { Shell } from '../../components/layout/Shell.jsx';
import { ouvreDonnees } from '../../components/DocumentsJoints.jsx';
import { ouvreVisionneuse } from '../../components/Visionneuse.jsx';
import { useLectureTicket } from '../../components/LectureTicket.jsx';
import { PhotoBtn } from '../../components/PhotoBtn.jsx';
import { QRCode } from '../../components/QRCode.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { StatBox } from '../../components/ui/StatBox.jsx';
import { FRAIS_CAT } from '../../constants/accounting';
import { jourDuPaiementEtat, paiementEtat, sansPaiementsEtat } from '../../lib/paiements-etat';
import { DepenseA4View } from '../documents/DepenseA4View.jsx';
import { dansLaPeriode, dateIso, TicketsA4 } from './TicketsA4.jsx';
import { depenseSansPiece } from '../../lib/rapprochement-sorties';
import { EnvoiDepenses } from './EnvoiDepenses.jsx';
import { tauxDepense, tvaDeLaDepense, tvaRecuperable } from '../../lib/vat';
import { DEVISE, fmt } from '../../lib/currency';
import { phraseDeConfiance } from '../../lib/ia';
import { localeDates, tr, trf } from '../../lib/i18n';
import {
  autresMois,
  corrigeLeMois,
  depensesPeriodiques,
  factureDeLaDepense,
  MOIS_ENTRE_PAIEMENTS,
  moisDepuis,
} from '../../lib/depenses-mensuelles';
import { moisCouverts } from '../../lib/depenses-reparties';
import { prepareLeJustificatif } from '../../lib/justificatif';
import { libellePaiement, PAIEMENTS } from '../../lib/paiement-depense';
import { swissQR } from '../../lib/swiss-qr';
import { universMagasin } from '../../lib/modules';
import { today, uid } from '../../lib/utils';
import { anneeDe, anneesDisponibles } from '../../lib/cloture';
import { idProvisoire } from '../../lib/identifiants';
import { C } from '../../theme/palette';

/**
 * Le rythme d'une dépense qui revient. La TVA, l'AVS et les assurances se
 * paient différemment d'une entreprise à l'autre : à l'année, au semestre, au
 * trimestre ou au mois. « Une seule fois », c'est une dépense ordinaire.
 */
const FREQUENCES = [
  ['', 'Une seule fois'],
  ['mensuel', 'Chaque mois'],
  ['trimestriel', 'Chaque trimestre'],
  ['semestriel', 'Chaque semestre'],
  ['annuel', 'Une fois par année'],
];

/**
 * « janvier 2026 », ou « janvier » sans l'année, dans la langue de l'écran.
 *
 * @param {number} n mois compté depuis l'an zéro (année × 12 + mois - 1)
 * @param {boolean} [avecAnnee] ajoute l'année
 * @returns {string} le nom du mois
 */
function nomDuMois(n, avecAnnee = true) {
  const d = new Date(Math.floor(n / 12), n % 12, 1);
  try {
    return new Intl.DateTimeFormat(localeDates(), {
      month: 'long',
      ...(avecAnnee ? { year: 'numeric' } : {}),
    }).format(d);
  } catch {
    return String((n % 12) + 1).padStart(2, '0') + (avecAnnee ? '.' + d.getFullYear() : '');
  }
}

/** Le mois `AAAA-MM` compté depuis l'an zéro (année × 12 + mois - 1). */
const rangDuMois = (v) => {
  const [a, m] = String(v || '')
    .split('-')
    .map(Number);
  return a && m ? a * 12 + m - 1 : null;
};

/**
 * La dépense inscrite sur son chantier, en facture fournisseur. Le fournisseur,
 * la catégorie et la TVA du ticket la suivent : sur le chantier, elle ne
 * gardait que son libellé et son montant, et sa TVA y était estimée au taux
 * normal.
 */
function factureDuChantier(l, catLib, id) {
  return {
    id,
    depenseId: l.id,
    fournisseur:
      String(l.fournisseur || '')
        .split(' — ')[0]
        .trim() ||
      l.libelle ||
      catLib,
    ref: l.libelle || '',
    cat: catLib,
    montant: +l.montant || 0,
    date: l.date,
    photo: l.piece || '',
    type: l.type,
    // Une dépense sans TVA (assurance, loyer, frais bancaires) part à 0 % :
    // sans taux, le chantier la comptait au taux normal.
    tauxTva: l.tva ?? (FRAIS_CAT[l.type] && !FRAIS_CAT[l.type].tva ? 0 : null),
    montantTva: l.montantTva ?? null,
  };
}

/** Vrai quand la dépense a sa facture ou son ticket, original ou copie. */
const aUnJustificatif = (x) => !!(x.piece || x.originalId);

/** Vrai quand le justificatif de la dépense est un PDF. */
const estUnPdf = (x) => x.pieceMime === 'application/pdf' || /\.pdf$/i.test(x.pieceNom || '');

/**
 * Ouvre la vraie facture de la dépense : le fichier d'origine tel qu'il a été
 * reçu — un PDF avec toutes ses pages, une photo en grand —, ou la copie
 * jointe quand l'original n'a pas été gardé.
 *
 * @param {object} x la dépense
 */
function ouvreJustificatif(x) {
  const titre = [
    String(x.fournisseur || '').split(' — ')[0] || x.libelle,
    DEVISE + ' ' + fmt(x.montant),
  ]
    .filter(Boolean)
    .join(' · ');
  if (x.originalId) {
    const adresse = depensesApi.adresseOriginal(x.originalId);
    if (!ouvreVisionneuse({ adresse, titre })) window.open(adresse, '_blank', 'noopener');
    return;
  }
  ouvreDonnees(x.piece, titre);
}

export function DepensesPage({
  onNav,
  soc,
  setSoc,
  chantiers,
  setChantiers,
  frais,
  setFrais,
  estMixte,
  univers,
  setUnivers,
  ouvrir,
}) {
  // « 📸 Scanner une dépense » de l'accueil arrive ici, le formulaire ouvert.
  const [add, setAdd] = useState(!!ouvrir?.nouvelle);
  const [fournForm, setFournForm] = useState(null);
  // Le justificatif d'une dépense de chantier devient une pièce jointe du
  // chantier, que sa ligne de facture fournisseur garde par référence.
  const versLeChantier = async (chantierId, donnees, nom, lignes) => {
    const id = Number(chantierId);
    if (!Number.isFinite(id) || !String(donnees).startsWith('data:')) return;
    try {
      const blob = await (await fetch(donnees)).blob();
      const fichier = new File(
        [blob],
        nom || (blob.type === 'application/pdf' ? 'piece.pdf' : 'piece.jpg'),
        {
          type: blob.type,
        },
      );
      const piece = await piecesApi.depose('CHANTIER', id, fichier, nom || '');
      const liees = new Set(lignes.map((l) => l.id));
      setChantiers((p) =>
        p.map((c) =>
          c.id !== id
            ? c
            : {
                ...c,
                factures: (c.factures || []).map((fa) =>
                  liees.has(fa.depenseId)
                    ? {
                        ...fa,
                        pieceId: piece.id,
                        photo: piece.fichier ? adresseApi(piece.fichier) : fa.photo,
                      }
                    : fa,
                ),
              },
        ),
      );
    } catch {
      // Sans réseau, la ligne reste sans justificatif ; la dépense garde le sien.
    }
  };
  const propres = soc && Array.isArray(soc.fournisseurs) ? soc.fournisseurs : [];
  // Un seul carnet : ceux des bons d'achat (nom, adresse, téléphone) s'y
  // ajoutent. Deux carnets séparés obligeaient à tout saisir deux fois.
  const [carnetBons, setCarnetBons] = useState([]);
  useEffect(() => {
    bonsAchatApi
      .fournisseurs()
      .then((l) => setCarnetBons(Array.isArray(l) ? l : []))
      .catch(() => {});
  }, []);
  const fournisseurs = [
    ...propres,
    ...carnetBons
      .filter(
        (b) => b?.nom && !propres.some((p) => (p.nom || '').toLowerCase() === b.nom.toLowerCase()),
      )
      .map((b) => ({
        id: 'bon-' + b.id,
        nom: b.nom,
        contact: '',
        tel: b.telephone || '',
        email: '',
        note: b.adresse || '',
        duCarnet: true,
      })),
  ];
  const [payQrId, setPayQrId] = useState(null);
  // Les séries arrêtées depuis l'ouverture de la page ; les autres le disent
  // elles-mêmes (serieArretee, relu du serveur).
  const [arretees, setArretees] = useState(() => new Set());
  const estArretee = (x) => !!x?.serie && (x.serieArretee || arretees.has(x.serie));
  // Le paiement le plus récent de chaque série : c'est lui qui porte l'arrêt,
  // une seule fois par série plutôt que sur chaque mois.
  const dernierDeLaSerie = new Map();
  frais.forEach((x) => {
    if (!x.serie) return;
    const d = dernierDeLaSerie.get(x.serie);
    const cle = (y) =>
      String(y.date || '')
        .split('.')
        .reverse()
        .join('');
    if (!d || cle(x) > cle(d)) dernierDeLaSerie.set(x.serie, x);
  });
  /**
   * Arrête les prochains paiements d'une série, après confirmation : ceux
   * déjà notés restent, le serveur n'en ajoute plus.
   *
   * @param {object} x un paiement de la série
   */
  const arreteLaSerie = (x) => {
    const base = String(x.libelle || '').replace(/\s+—\s+\d{2}\.\d{4}$/, '');
    if (
      !window.confirm(
        trf('Arrêter les prochains paiements de « {0} » ? Ceux déjà notés restent.', base),
      )
    ) {
      return;
    }
    depensesApi
      .arreteSerie(x.serie)
      .then(() => setArretees((s) => new Set(s).add(x.serie)))
      .catch(() =>
        window.alert(tr('L’arrêt n’a pas pu être enregistré. Réessaie dans un instant.')),
      );
  };
  /** « ⏹ Arrêter les prochains paiements », ou l'arrêt déjà fait. */
  const boutonArret = (x) =>
    estArretee(x) ? (
      <div style={{ fontSize: 8, color: C.muted, fontWeight: 700 }}>
        {tr('⏹ Prochains paiements arrêtés')}
      </div>
    ) : (
      <button
        type="button"
        onClick={() => arreteLaSerie(x)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: 8.5,
          fontWeight: 700,
          color: C.red,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {tr('⏹ Arrêter les prochains paiements')}
      </button>
    );
  const [a4, setA4] = useState(null);
  const magasin = universMagasin({ soc, estMixte, univers });
  // « Envoyer » : les dépenses cochées partent en un PDF, une seule ou toutes.
  const [envoi, setEnvoi] = useState(false);
  const [cochees, setCochees] = useState([]);
  // Ce que l'on envoie : une période et un type ; les dépenses qui en sont
  // arrivent toutes cochées, on décoche celles qu'on ne veut pas.
  const [filtreEnvoi, setFiltreEnvoi] = useState({
    periode: 'tout',
    du: '',
    au: '',
    mois: [],
    type: 'tous',
  });
  const correspond = (f) => (x) =>
    (f.type === 'tous' || x.type === f.type) &&
    dansLaPeriode(dateIso(x.date), f.periode, f.du, f.au, f.mois);
  const fraisEnvoi = frais.filter(correspond(filtreEnvoi));
  const changeFiltreEnvoi = (f) => {
    setFiltreEnvoi(f);
    setCochees(frais.filter(correspond(f)).map((x) => x.id));
  };
  const bascule = (id) =>
    setCochees((p) => (p.includes(id) ? p.filter((y) => y !== id) : [...p, id]));
  const [ticketsA4, setTicketsA4] = useState(false);
  const [errPiece, setErrPiece] = useState('');
  // La photo d'origine part dès qu'elle est jointe ; la dépense attend qu'elle
  // soit gardée pour s'enregistrer avec elle.
  const [envoiOriginal, setEnvoiOriginal] = useState(false);
  const derniereOriginale = useRef(0);
  const [noteLecture, setNoteLecture] = useState('');
  const [f, setF] = useState({
    type: 'vehicule',
    libelle: '',
    montant: 0,
    date: today(),
    piece: '',
    pieceNom: '',
    pieceMime: '',
  });
  // Les paiements à l'État (TVA, AVS, impôts) sont listés, mais ne sont pas
  // des dépenses : ni dans le total ni dans la TVA récupérable.
  // Par exercice, comme la Compta : le total additionnait toutes les années.
  const annees = anneesDisponibles([], frais);
  const [annee, setAnnee] = useState(annees[0]);
  const charges = sansPaiementsEtat(frais).filter((x) => anneeDe(x) === annee);
  const total = charges.reduce((s, x) => s + (+x.montant || 0), 0);
  // Une dépense qui revient : les paiements notés à l'enregistrement, du
  // premier jusqu'à ce mois-ci, comme le loyer payé chaque mois.
  const pas = paiementEtat(f.type) ? 0 : MOIS_ENTRE_PAIEMENTS[f.frequence] || 0;
  const notes = pas ? moisDepuis(f.depuis, new Date(), pas) : [];
  const rangs = notes.map(({ annee, mois }) => annee * 12 + mois - 1);
  const premier = rangDuMois(f.depuis);
  // Ni la TVA ni l'AVS ni les impôts ne se répartissent : ce ne sont pas des charges.
  const repartissable = pas > 1 && !paiementEtat(f.type);
  const tvaRec = tvaRecuperable(charges, FRAIS_CAT, soc);
  // Par compte, sur les mêmes dépenses que le total : les paiements à l'État
  // (comptes 2200, 2270, 2208) n'y sont plus, et la somme des lignes vaut le
  // total. Les catégories d'un même compte sont réunies.
  const parCompte = new Map();
  charges.forEach((x) => {
    const c = FRAIS_CAT[x.type] || {};
    const k = c.compte || '—';
    const g = parCompte.get(k) || { k, compte: c.compte || '', libs: new Set(), total: 0 };
    g.libs.add((c.icon ? c.icon + ' ' : '') + tr(c.lib || x.type || ''));
    g.total += +x.montant || 0;
    parCompte.set(k, g);
  });
  const parCat = [...parCompte.values()]
    .map((g) => ({ ...g, lib: [...g.libs].join(' · ') }))
    .filter((c) => c.total > 0)
    .sort((a, b) => String(a.compte).localeCompare(String(b.compte)));
  /**
   * Joint un justificatif a la depense en cours de saisie.
   *
   * La piece voyage encodee dans le corps de la depense, et le serveur y pose
   * une limite d'un megaoctet. Une photo de telephone la depasse sans peine :
   * sans reduction, la depense partait avec sa piece et revenait refusee, au
   * moment precis ou l'on voulait la classer.
   *
   * @param {Blob} fichier fichier choisi ou photographie
   * @param {string} [secours] apercu a defaut de fichier, sur les navigateurs
   *   ou la prise de vue n'en rend pas
   */
  const joint = async (fichier, secours) => {
    setErrPiece('');
    if (fichier) garde(fichier);
    // Avec le Pack IA, scanner ou joindre le ticket le lit aussi : montant,
    // date, fournisseur et son adresse, TVA et catégorie se remplissent, et la
    // pièce est jointe dans le même geste.
    if (fichier && lecture.actif) {
      await lecture.lit(fichier);
      return;
    }
    if (!fichier) {
      if (secours) {
        setF((p) => ({ ...p, piece: secours, pieceNom: 'photo.jpg', pieceMime: 'image/jpeg' }));
      }
      return;
    }
    try {
      const piece = await prepareLeJustificatif(fichier);
      setF((p) => ({ ...p, ...piece }));
    } catch (e) {
      setErrPiece(e?.message || 'Le justificatif n’a pas pu être joint.');
    }
  };

  /**
   * Garde la photo ou le PDF d'origine, tel que pris : la copie jointe à la
   * dépense est réduite pour l'affichage, celle-ci reste lisible pour un
   * contrôle fiscal, une fois le ticket papier jeté.
   *
   * @param {Blob} fichier fichier choisi ou photographié
   */
  const garde = (fichier) => {
    const numero = ++derniereOriginale.current;
    setEnvoiOriginal(true);
    setF((p) => ({ ...p, originalId: null }));
    depensesApi
      .envoieOriginal(fichier, fichier.name)
      .then((r) => {
        if (numero === derniereOriginale.current)
          setF((p) => ({ ...p, originalId: r?.id ?? null }));
      })
      .catch(() => {
        if (numero === derniereOriginale.current) {
          setErrPiece(
            tr('La photo d’origine n’a pas pu être gardée : seule la copie réduite sera jointe.'),
          );
        }
      })
      .finally(() => {
        if (numero === derniereOriginale.current) setEnvoiOriginal(false);
      });
  };

  // Vrai dès que la catégorie est touchée à la main ; le formulaire, refermé
  // puis rouvert, la garde comme le reste. Il repart de zéro à l'enregistrement.
  const categorieChoisie = useRef(false);
  /**
   * Reporte ce que le Pack IA a lu sur le ticket dans le formulaire.
   *
   * Seuls les champs lus sont remplis ; ce que l'utilisateur avait deja saisi
   * reste s'il n'y a rien de mieux. La categorie n'est reprise que si l'ecran
   * la connait, et la piece est jointe telle qu'elle a ete lue.
   *
   * @param {object} lu ce que le serveur a lu
   * @param {object|null} piece piece prete a joindre, ou null
   */
  const litLeTicket = (lu, piece) => {
    // La catégorie touchée à la main reste : le patron choisissait « Café »,
    // prenait le ticket en photo, et la lecture le rangeait sous « Carburant ».
    const garde = categorieChoisie.current;
    const tauxLus = [
      ...new Set((Array.isArray(lu.tauxTva) ? lu.tauxTva : []).map(Number).filter(Number.isFinite)),
    ];
    setF((p) => ({
      ...p,
      libelle: lu.libelle || lu.fournisseur || p.libelle,
      montant: lu.montantTtc != null ? +lu.montantTtc : p.montant,
      date: lu.date ? isoVersDate(lu.date) : p.date,
      type: !garde && lu.categorie && FRAIS_CAT[lu.categorie] ? lu.categorie : p.type,
      // Le nom et l'adresse du commerce, comme imprimés sur le ticket.
      fournisseur: lu.fournisseur
        ? [lu.fournisseur, lu.adresse].filter(Boolean).join(' — ').slice(0, 200)
        : p.fournisseur || '',
      iban: lu.iban || p.iban || '',
      // Chaque lecture est un nouveau ticket : rien de l'ancien ne reste. Un
      // seul taux lu fait foi ; plusieurs (pommes à 2.6 %, vin à 8.1 %) sans
      // le total de TVA : le premier taux comptait pour tout le ticket
      // (0.51 au lieu de 1.20 sur 20.–). Le taux de la catégorie reste, et
      // le patron est prié de taper la TVA totale du ticket.
      tva: tauxLus.length === 1 ? tauxLus[0] : null,
      // Le total de TVA imprimé sur le ticket : c'est lui qui se récupère,
      // même quand le ticket mêle plusieurs taux.
      montantTva: lu.montantTva != null ? +lu.montantTva : null,
      // « Espèces », « Visa », « TWINT » imprimés sur le ticket.
      paiement: lu.paiement || p.paiement || '',
      ...(piece
        ? { piece: piece.piece, pieceNom: piece.pieceNom, pieceMime: piece.pieceMime }
        : {}),
    }));
    setNoteLecture(
      [
        phraseDeConfiance(lu.confiance),
        tauxLus.length > 1 && lu.montantTva == null
          ? tr('Plusieurs taux de TVA sur ce ticket : tape la TVA totale imprimée.')
          : '',
      ]
        .filter(Boolean)
        .join(' '),
    );
  };
  const lecture = useLectureTicket(litLeTicket);

  const exportCSV = () => {
    try {
      const rows = [['Date', 'Compte', 'Catégorie', 'Libellé', 'Montant CHF', 'TVA %']];
      frais.forEach((x) => {
        const c = FRAIS_CAT[x.type] || {};
        rows.push([
          x.date,
          c.compte || '',
          c.lib || x.type,
          x.libelle,
          String(x.montant),
          String(tauxDepense(x, FRAIS_CAT, soc)),
        ]);
      });
      const csv = rows
        .map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(';'))
        .join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'depenses-swisstech.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {}
  };
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="💸 Dépenses & frais"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      {a4 && <DepenseA4View dep={a4} soc={soc} chantiers={chantiers} onClose={() => setA4(null)} />}
      {annees.length > 1 && (
        <div style={{ display: 'flex', gap: 5, marginBottom: 9 }}>
          {annees.map((a) => (
            <button
              key={a}
              type="button"
              role="tab"
              aria-selected={a === annee}
              onClick={() => setAnnee(a)}
              style={{
                padding: '5px 11px',
                borderRadius: 8,
                border: '1px solid ' + C.border,
                background: a === annee ? C.navy : C.white,
                color: a === annee ? '#fff' : C.ink,
                fontWeight: 700,
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              {a}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <StatBox label="Total dépenses" value={DEVISE + ' ' + fmt(total)} color={C.red} icon="💸" />
        <StatBox
          label="TVA récupérable"
          value={DEVISE + ' ' + fmt(tvaRec)}
          color={C.teal}
          icon="🇨🇭"
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <Btn
            label={add ? 'Fermer' : '＋ Nouvelle dépense'}
            color={C.navy}
            full
            onClick={() => setAdd((a) => !a)}
          />
        </div>
        <Btn label="⬇ CSV" ghost color={C.navy} onClick={exportCSV} />
        <Btn
          label={'🖨 ' + tr('Tickets A4')}
          ghost
          color={C.navy}
          onClick={() => setTicketsA4((t) => !t)}
        />
        <Btn
          label={tr('📤 Envoyer')}
          ghost
          color={C.navy}
          onClick={() => {
            const depart = { periode: 'tout', du: '', au: '', mois: [], type: 'tous' };
            setFiltreEnvoi(depart);
            setCochees(envoi ? [] : frais.map((x) => x.id));
            setEnvoi((e) => !e);
          }}
        />
      </div>
      {ticketsA4 && <TicketsA4 frais={frais} soc={soc} onClose={() => setTicketsA4(false)} />}
      {add && (
        <Card style={{ marginBottom: 12, border: '2px solid ' + C.navy }}>
          <Bar icon="💸" title="Nouvelle dépense" color={C.navy} />
          <div style={{ padding: 13 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: C.muted,
                marginBottom: 6,
                textTransform: 'uppercase',
              }}
            >
              Catégorie
            </div>
            {/* Les dépenses, puis à part ce qui est payé à l'État : TVA, AVS,
                impôts ne sont pas des dépenses, ils remplissent « Déjà payé ». */}
            {[
              Object.keys(FRAIS_CAT).filter(
                (k) =>
                  !paiementEtat(k) &&
                  // Un magasin n'a pas de sous-traitance : la catégorie ne
                  // s'y propose pas (une dépense déjà rangée là la garde).
                  !(k === 'soustraitance' && magasin && f.type !== 'soustraitance'),
              ),
              Object.keys(FRAIS_CAT).filter((k) => paiementEtat(k)),
            ].map((cles, groupe) => (
              <div key={groupe}>
                {groupe === 1 && (
                  <div style={{ fontSize: 9, fontWeight: 800, color: C.grey, margin: '2px 0 6px' }}>
                    🏛 {tr('Payé à l’État')}{' '}
                    <span style={{ fontWeight: 600, color: C.muted }}>
                      · {tr('pas une dépense : compté dans « Déjà payé » sur l’accueil')}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 6,
                    marginBottom: 10,
                  }}
                >
                  {cles.map((k) => {
                    const c = FRAIS_CAT[k];
                    const on = f.type === k;
                    return (
                      <div
                        key={k}
                        onClick={() => {
                          categorieChoisie.current = true;
                          // TVA, AVS, impôts : un paiement, un montant — jamais
                          // répété tout seul (2 × CHF 1'952.95 de TVA comptés).
                          setF((p) =>
                            paiementEtat(k)
                              ? { ...p, type: k, frequence: '', repartir: false }
                              : { ...p, type: k },
                          );
                        }}
                        style={{
                          padding: '8px 4px',
                          borderRadius: 9,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: on ? C.navy : C.white,
                          border: '1px solid ' + C.border,
                        }}
                      >
                        <div style={{ fontSize: 15 }}>{c.icon}</div>
                        <div
                          style={{ fontSize: 7.5, fontWeight: 700, color: on ? '#fff' : C.grey }}
                        >
                          {c.lib}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <Inp
              label="Libellé"
              value={f.libelle}
              onChange={(e) => setF((p) => ({ ...p, libelle: e.target.value }))}
            />
            <Inp
              label="Fournisseur et adresse"
              value={f.fournisseur || ''}
              onChange={(e) => setF((p) => ({ ...p, fournisseur: e.target.value }))}
              placeholder="Ex. Shell — Route de Lausanne 1, 1023 Crissier"
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Inp
                label={'Montant ' + DEVISE}
                type="number"
                value={f.montant}
                onChange={(e) => setF((p) => ({ ...p, montant: +e.target.value }))}
              />
              <Inp
                label="Date"
                value={f.date}
                onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))}
              />
            </div>
            {/* Payé en espèces ou par carte : un café payé cash, l'essence à la carte. */}
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: C.muted,
                marginBottom: 5,
                textTransform: 'uppercase',
              }}
            >
              {tr('Moyen de paiement')}
            </div>
            <div style={{ display: 'flex', gap: 7, marginBottom: 10 }}>
              {PAIEMENTS.map(({ k, lib }) => {
                const on = f.paiement === k;
                return (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setF((p) => ({ ...p, paiement: on ? '' : k }))}
                    style={{
                      flex: 1,
                      padding: '9px 6px',
                      borderRadius: 9,
                      border: '1.5px solid ' + (on ? C.navy : C.border),
                      background: on ? C.navy : C.white,
                      color: on ? '#fff' : C.ink,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {tr(lib)}
                  </button>
                );
              })}
            </div>
            {/* Le jour du paiement : le jour même pour un ticket payé en espèces ou
                par carte ; pour une facture payée plus tard, à la main, ou trouvé
                tout seul dans le relevé de banque. */}
            {!(f.editId == null && f.frequence) && (
              <Inp
                label="Payé le"
                value={f.payeLe || ''}
                placeholder={
                  f.paiement === 'especes' || f.paiement === 'carte' ? f.date : 'JJ.MM.AAAA'
                }
                onChange={(e) => setF((p) => ({ ...p, payeLe: e.target.value }))}
              />
            )}
            {f.editId != null &&
              autresMois(
                frais,
                frais.find((y) => y.id === f.editId),
              ).length > 0 && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    border: '1px solid ' + (f.tousLesMois ? C.navy : C.border),
                    background: f.tousLesMois ? C.navyXL : C.white,
                    borderRadius: 9,
                    padding: '9px 11px',
                    marginBottom: 10,
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.navy,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!f.tousLesMois}
                    onChange={(e) => setF((p) => ({ ...p, tousLesMois: e.target.checked }))}
                  />
                  {trf(
                    f.frequence && f.frequence !== 'mensuel'
                      ? '🔁 Appliquer aussi aux {0} autres paiements'
                      : '🔁 Appliquer aussi aux {0} autres mois',
                    autresMois(
                      frais,
                      frais.find((y) => y.id === f.editId),
                    ).length,
                  )}
                </label>
              )}
            {/* Une assurance payée à l'année, déjà enregistrée : sa charge se
                répartit sur ses mois, ou plus — la case se corrige ici. */}
            {f.editId != null && MOIS_ENTRE_PAIEMENTS[f.frequence] > 1 && !paiementEtat(f.type) && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: '1px solid ' + C.border,
                  borderRadius: 9,
                  padding: '9px 11px',
                  marginBottom: 10,
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.navy,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={moisCouverts(f) > 1}
                  onChange={(e) =>
                    setF((p) => ({
                      ...p,
                      moisCouverts: e.target.checked ? MOIS_ENTRE_PAIEMENTS[p.frequence] : null,
                    }))
                  }
                />
                {tr('Répartir sur les mois couverts')}
              </label>
            )}
            {/* Une série enregistrée se poursuit toute seule : on l'arrête ici. */}
            {f.editId != null && f.serie && (
              <div style={{ marginBottom: 10, fontSize: 11 }}>{boutonArret(f)}</div>
            )}
            {/* Un paiement à l'État règle une période : le décompte de TVA du
                dernier trimestre se paie en février de l'année suivante. Il se
                range dans l'année qu'il règle, au choix du patron. */}
            {paiementEtat(f.type) &&
              /^\d{2}\.\d{2}\.\d{4}$/.test(f.date || '') &&
              (() => {
                const an = +f.date.slice(6);
                const choisi = +jourDuPaiementEtat(f).slice(6) || an;
                return (
                  <label style={{ display: 'block', marginBottom: 10, fontSize: 11 }}>
                    <span style={{ display: 'block', fontWeight: 700, marginBottom: 4 }}>
                      {tr('Ce paiement est pour l’année')}
                    </span>
                    <select
                      value={choisi}
                      onChange={(e) =>
                        setF((p) => ({ ...p, dateCompta: '31.12.' + e.target.value }))
                      }
                      style={{ padding: '6px 8px', borderRadius: 8, fontSize: 12 }}
                    >
                      {[an - 1, an].map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })()}
            {/* Un loyer, une assurance : payé chaque mois, trimestre, semestre
                ou année, saisi une fois. Pas la TVA, l'AVS ni les impôts :
                chaque décompte a son montant, et « chaque semestre » recopiait
                le même paiement depuis le premier mois — la TVA payée comptait
                deux fois. Chaque paiement à l'État se note quand il est fait. */}
            {f.editId == null && !paiementEtat(f.type) && (
              <div
                style={{
                  border: '1px solid ' + (pas ? C.navy : C.border),
                  background: pas ? C.navyXL : C.white,
                  borderRadius: 9,
                  padding: '9px 11px',
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, marginBottom: 7 }}>
                  {tr('🔁 Je paie ce montant…')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {FREQUENCES.map(([k, lib]) => {
                    const on = (f.frequence || '') === k;
                    return (
                      <button
                        key={k || 'une'}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          setF((p) => ({
                            ...p,
                            frequence: k,
                            // L'assurance de l'année se compte un douzième par
                            // mois : le patron l'a demandé d'office.
                            repartir: (MOIS_ENTRE_PAIEMENTS[k] || 0) > 1,
                            depuis: p.depuis || new Date().getFullYear() + '-01',
                          }))
                        }
                        style={{
                          padding: '7px 11px',
                          borderRadius: 18,
                          border: '1.5px solid ' + (on ? C.navy : C.border),
                          background: on ? C.navy : C.white,
                          color: on ? '#fff' : C.ink,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {(on ? '✓ ' : '') + tr(lib)}
                      </button>
                    );
                  })}
                </div>
                {pas > 0 && (
                  <>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.muted,
                        margin: '9px 0 4px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {tr('Premier paiement')}
                    </div>
                    <select
                      aria-label={tr('Premier paiement')}
                      value={f.depuis}
                      onChange={(e) => setF((p) => ({ ...p, depuis: e.target.value }))}
                      style={{
                        width: '100%',
                        border: '1px solid ' + C.border,
                        borderRadius: 9,
                        padding: '9px',
                        fontSize: 13,
                        boxSizing: 'border-box',
                      }}
                    >
                      {moisDepuis(new Date().getFullYear() - 1 + '-01')
                        .reverse()
                        .map(({ annee, mois }) => {
                          const v = annee + '-' + String(mois).padStart(2, '0');
                          const nom = nomDuMois(annee * 12 + mois - 1);
                          return (
                            <option key={v} value={v}>
                              {nom.charAt(0).toUpperCase() + nom.slice(1)}
                            </option>
                          );
                        })}
                    </select>
                    {repartissable && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                          marginTop: 9,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!f.repartir}
                          onChange={(e) => setF((p) => ({ ...p, repartir: e.target.checked }))}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <span
                            style={{
                              display: 'block',
                              fontSize: 11,
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            {tr('Répartir sur les mois couverts')}
                          </span>
                          {premier != null && (
                            <span style={{ fontSize: 9.5, color: C.grey, lineHeight: 1.5 }}>
                              {trf(
                                '{0} payés en {1} → comptés {2} par mois, de {1} à {3}.',
                                DEVISE + ' ' + fmt(+f.montant || 0),
                                nomDuMois(premier, false),
                                DEVISE + ' ' + fmt((+f.montant || 0) / pas),
                                nomDuMois(premier + pas - 1, false),
                              )}
                            </span>
                          )}
                        </span>
                      </label>
                    )}
                    {pas > 1 && paiementEtat(f.type) && (
                      <div style={{ fontSize: 9.5, color: C.grey, marginTop: 7, lineHeight: 1.5 }}>
                        {tr(
                          'Payé à l’État : compté en entier le jour du paiement, dans « Déjà payé ».',
                        )}
                      </div>
                    )}
                    {rangs.length > 0 && (
                      <div
                        style={{
                          background: C.white,
                          borderRadius: 8,
                          padding: '8px 10px',
                          marginTop: 9,
                          fontSize: 9.5,
                          color: C.grey,
                          lineHeight: 1.5,
                        }}
                      >
                        {pas === 1 ? (
                          <div style={{ fontWeight: 700, color: C.ink }}>
                            {trf(
                              '{0} mois jusqu’à ce mois-ci : une dépense de {1} par mois, total {2}.',
                              notes.length,
                              DEVISE + ' ' + fmt(+f.montant || 0),
                              DEVISE + ' ' + fmt((+f.montant || 0) * notes.length),
                            )}
                          </div>
                        ) : (
                          <>
                            <div style={{ fontWeight: 700, color: C.ink }}>
                              {trf(
                                'Notés maintenant : {0} paiement(s), total {1}',
                                rangs.length,
                                DEVISE + ' ' + fmt((+f.montant || 0) * rangs.length),
                              )}
                            </div>
                            <div>{rangs.map((n) => nomDuMois(n)).join(' · ')}</div>
                          </>
                        )}
                        {/* Le serveur note la suite chaque nuit, jusqu'à l'arrêt. */}
                        <div style={{ marginTop: 3 }}>
                          {trf(
                            'Les prochains paiements se notent tout seuls : {0} …',
                            [1, 2]
                              .map((k) => nomDuMois(rangs[rangs.length - 1] + k * pas))
                              .join(' · '),
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <Inp
              label={'TVA payée ' + DEVISE + ' (sur le ticket)'}
              type="number"
              value={f.montantTva ?? ''}
              onChange={(e) =>
                setF((p) => ({
                  ...p,
                  montantTva: e.target.value === '' ? null : +e.target.value,
                }))
              }
              placeholder="Vide : calculée au taux de la catégorie"
            />
            <Inp
              label="IBAN du fournisseur (pour payer)"
              value={f.iban || ''}
              onChange={(e) => setF((p) => ({ ...p, iban: e.target.value }))}
              placeholder="CH.. (optionnel)"
            />
            {!universMagasin({ soc, estMixte, univers }) &&
              !paiementEtat(f.type) &&
              (chantiers || []).length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      margin: '4px 0 6px',
                      textTransform: 'uppercase',
                    }}
                  >
                    🏗️ Chantier concerné (optionnel)
                  </div>
                  <select
                    value={f.chantierId || ''}
                    onChange={(e) => setF((p) => ({ ...p, chantierId: e.target.value }))}
                    style={{
                      width: '100%',
                      border: '1px solid ' + C.border,
                      borderRadius: 9,
                      padding: '11px',
                      fontSize: 13,
                      marginBottom: 8,
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">— Aucun (frais général) —</option>
                    {(chantiers || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {nomDuChantier(c, chantiers)}
                      </option>
                    ))}
                  </select>
                </>
              )}
            {/* Le justificatif tout en bas, juste avant Enregistrer (demande
                du patron) : scanné, il remplit les champs au-dessus. */}
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: C.muted,
                margin: '4px 0 6px',
                textTransform: 'uppercase',
              }}
            >
              Justificatif (photo ou pièce jointe)
            </div>
            {f.piece ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: C.navyXL,
                  borderRadius: 9,
                  padding: '9px 11px',
                  marginBottom: 10,
                }}
              >
                {(f.pieceMime || '').startsWith('image/') ? (
                  <img
                    src={f.piece}
                    alt=""
                    style={{
                      width: 42,
                      height: 42,
                      objectFit: 'cover',
                      borderRadius: 7,
                      border: '1px solid ' + C.border,
                    }}
                  />
                ) : (
                  <span style={{ fontSize: 24 }}>📄</span>
                )}
                <div
                  style={{
                    flex: 1,
                    fontSize: 9,
                    color: C.grey,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {f.pieceNom || 'Justificatif joint'}
                </div>
                <button
                  onClick={() => {
                    derniereOriginale.current += 1;
                    setEnvoiOriginal(false);
                    setF((p) => ({
                      ...p,
                      piece: '',
                      pieceNom: '',
                      pieceMime: '',
                      originalId: null,
                      // La TVA lue sur le ticket retiré part avec lui.
                      tva: null,
                      montantTva: null,
                    }));
                  }}
                  style={{
                    background: C.redL,
                    border: 'none',
                    borderRadius: 7,
                    color: C.red,
                    padding: '5px 9px',
                    cursor: 'pointer',
                  }}
                >
                  Retirer
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <PhotoBtn
                    label="📸 Scanner le justificatif"
                    color={C.navy}
                    full
                    onPhoto={(data, fichier) => joint(fichier, data)}
                  />
                </div>
                <label
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                    border: '1px dashed ' + C.teal,
                    borderRadius: 9,
                    padding: '11px',
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: C.teal,
                    cursor: 'pointer',
                  }}
                >
                  📎 Pièce jointe
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    aria-label="Justificatif à joindre"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const fl = e.target.files && e.target.files[0];
                      e.target.value = '';
                      if (fl) joint(fl);
                    }}
                  />
                </label>
              </div>
            )}
            {errPiece && (
              <div
                role="alert"
                style={{
                  marginBottom: 10,
                  fontSize: 9.5,
                  color: C.red,
                  background: C.red + '15',
                  borderRadius: 7,
                  padding: '7px 10px',
                  lineHeight: 1.5,
                }}
              >
                {errPiece}
              </div>
            )}
            {/*
              Absente de la maquette, la lecture par le Pack IA vient apres le
              justificatif, pour ne pas en deplacer les champs. Le Pack IA lit le ticket et pre-remplit le formulaire ; la piece
              lue est jointe dans le meme geste. Rien n'est enregistre avant que
              l'utilisateur n'ait verifie et appuye sur Enregistrer.
            */}
            {lecture.enCours && (
              <div
                role="status"
                style={{
                  marginBottom: 10,
                  fontSize: 9.5,
                  color: C.purple,
                  background: C.purpleL,
                  borderRadius: 7,
                  padding: '7px 10px',
                }}
              >
                📷 Lecture du ticket…
              </div>
            )}
            {lecture.etat && !lecture.enCours && (
              <div
                role="alert"
                style={{ fontSize: 9.5, color: C.red, marginBottom: 10, lineHeight: 1.5 }}
              >
                {lecture.etat}
              </div>
            )}
            {!lecture.actif && !f.piece && (
              <div style={{ fontSize: 9, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
                Pour que le montant, la date et le fournisseur se remplissent tout seuls au scan,
                active le Pack IA dans Réglages.
              </div>
            )}
            {noteLecture && (
              <div
                style={{
                  marginBottom: 10,
                  fontSize: 9.5,
                  color: C.purple,
                  background: C.purpleL,
                  borderRadius: 7,
                  padding: '7px 10px',
                  lineHeight: 1.5,
                }}
              >
                {noteLecture}
              </div>
            )}
            <Btn
              label={envoiOriginal ? tr('Envoi de la photo d’origine…') : '✓ Enregistrer'}
              color={C.navy}
              full
              // Pendant la lecture du ticket : sa fin remplirait un formulaire
              // déjà enregistré, et le même ticket partait deux fois.
              disabled={!f.libelle || !f.montant || envoiOriginal || lecture.enCours}
              onClick={() => {
                // Une dépense corrigée garde son identifiant ; sa copie sur le
                // chantier (facture fournisseur) suit la correction.
                if (f.editId != null) {
                  const ancienne = frais.find((y) => y.id === f.editId);
                  const {
                    editId,
                    mensuel: _m2,
                    depuis: _d2,
                    repartir: _r2,
                    tousLesMois,
                    ...maj
                  } = f;
                  const nouvelle = {
                    ...ancienne,
                    ...maj,
                    id: editId,
                    // Un paiement à l'État n'est pas une facture de chantier.
                    ...(paiementEtat(maj.type) ? { chantierId: '' } : {}),
                  };
                  // Payé en espèces ou par carte, le ticket l'est le jour de
                  // son achat : corriger la date du ticket corrige celle du
                  // paiement ; passé à « banque », il attend le relevé. La
                  // date du paiement restait l'ancienne.
                  const comptant = (x) => x?.paiement === 'especes' || x?.paiement === 'carte';
                  const payeLeJourMeme =
                    comptant(ancienne) && (!ancienne?.payeLe || ancienne.payeLe === ancienne.date);
                  if (payeLeJourMeme && (maj.payeLe ?? ancienne?.payeLe) === ancienne?.payeLe) {
                    nouvelle.payeLe = comptant(nouvelle) ? nouvelle.date : '';
                  }
                  // Les autres mois de la série, corrigés d'un coup si demandé.
                  const serie = tousLesMois
                    ? new Set(autresMois(frais, ancienne).map((y) => y.id))
                    : new Set();
                  setFrais((p) =>
                    p.map((y) =>
                      y.id === editId ? nouvelle : serie.has(y.id) ? corrigeLeMois(y, nouvelle) : y,
                    ),
                  );
                  if (
                    typeof setChantiers === 'function' &&
                    (ancienne?.chantierId || nouvelle.chantierId)
                  ) {
                    const libDe = (x) => (FRAIS_CAT[x?.type] || {}).lib || x?.type;
                    setChantiers((p) =>
                      p.map((c) => {
                        let fact = c.factures || [];
                        // La dépense corrigée et, si demandé, les autres mois
                        // de la série : chacun remplace sa facture sur le
                        // chantier. Seule celle du mois touché suivait, les
                        // autres gardaient l'ancien montant.
                        const paires = [
                          [ancienne, nouvelle],
                          ...frais
                            .filter((y) => serie.has(y.id))
                            .map((y) => [y, corrigeLeMois(y, nouvelle)]),
                        ];
                        paires.forEach(([avant, apres], n) => {
                          if (avant?.chantierId && String(c.id) === String(avant.chantierId)) {
                            const i = factureDeLaDepense(fact, avant, libDe(avant));
                            if (i >= 0) fact = fact.filter((_, k) => k !== i);
                          }
                          if (apres.chantierId && String(c.id) === String(apres.chantierId)) {
                            fact = [
                              factureDuChantier(apres, libDe(apres), Date.now() + n),
                              ...fact,
                            ];
                          }
                        });
                        return fact === c.factures ? c : { ...c, factures: fact };
                      }),
                    );
                  }
                  setF({
                    type: 'vehicule',
                    libelle: '',
                    montant: 0,
                    date: today(),
                    piece: '',
                    pieceNom: '',
                    pieceMime: '',
                  });
                  setAdd(false);
                  return;
                }
                // Un ticket scanné pour une dépense déjà notée sans pièce
                // (depuis le relevé bancaire) : il s'y joint, au lieu d'en
                // créer une seconde comptée deux fois.
                const dejaNotee = !pas && (f.piece || f.originalId) && depenseSansPiece(frais, f);
                if (
                  dejaNotee &&
                  window.confirm(
                    trf(
                      'Cette dépense est déjà notée : {0} · {1} du {2}. Y joindre ce justificatif au lieu d’en créer une deuxième ?',
                      dejaNotee.libelle,
                      DEVISE + ' ' + fmt(dejaNotee.montant),
                      dejaNotee.date,
                    ),
                  )
                ) {
                  setFrais((p) =>
                    p.map((y) =>
                      y.id === dejaNotee.id
                        ? {
                            ...y,
                            piece: f.piece,
                            pieceNom: f.pieceNom,
                            pieceMime: f.pieceMime,
                            originalId: f.originalId ?? y.originalId ?? null,
                            fournisseur: y.fournisseur || f.fournisseur || '',
                            montantTva: f.montantTva ?? y.montantTva ?? null,
                          }
                        : y,
                    ),
                  );
                  setF({
                    type: 'vehicule',
                    libelle: '',
                    montant: 0,
                    date: today(),
                    piece: '',
                    pieceNom: '',
                    pieceMime: '',
                  });
                  setNoteLecture('');
                  categorieChoisie.current = false;
                  setAdd(false);
                  return;
                }
                // Payée chaque mois, trimestre, semestre ou année : une dépense
                // par paiement depuis le premier.
                const { mensuel: _m, depuis: _d, repartir: _r, ...seule } = f;
                // Chaque paiement se fait à son jour : la banque le dira, un à un.
                const lignes = pas
                  ? depensesPeriodiques({ ...f, payeLe: '' }, f.depuis, f.frequence, new Date(), {
                      repartir: !!f.repartir,
                    })
                  : [paiementEtat(f.type) ? { ...seule, frequence: '', serie: null } : seule];
                const etat = paiementEtat(f.type);
                // Un identifiant provisoire unique : compté sur la longueur de
                // la liste, il retombait sur celui d'une dépense pas encore
                // envoyée, et effacer l'une effaçait l'autre.
                const nouvelles = lignes.map((l) => ({
                  ...l,
                  ...(etat ? { chantierId: '' } : {}),
                  id: idProvisoire(),
                }));
                setFrais((p) => [...nouvelles, ...p]);
                if (f.chantierId && !etat && typeof setChantiers === 'function') {
                  const catLib = (FRAIS_CAT[f.type] || {}).lib || f.type;
                  setChantiers((p) =>
                    p.map((c) =>
                      String(c.id) === String(f.chantierId)
                        ? {
                            ...c,
                            factures: [
                              ...nouvelles.map((l, i) =>
                                factureDuChantier(l, catLib, Date.now() + i),
                              ),
                              ...(c.factures || []),
                            ],
                          }
                        : c,
                    ),
                  );
                }
                // Le justificatif suit sur le chantier : gardé dans la ligne,
                // il disparaissait au rechargement.
                if (f.chantierId && !etat && f.piece && typeof setChantiers === 'function') {
                  versLeChantier(f.chantierId, f.piece, f.pieceNom, nouvelles);
                }
                setF({
                  type: 'vehicule',
                  libelle: '',
                  montant: 0,
                  date: today(),
                  piece: '',
                  pieceNom: '',
                  pieceMime: '',
                });
                setNoteLecture('');
                categorieChoisie.current = false;
                setAdd(false);
              }}
            />
          </div>
        </Card>
      )}
      <Card style={{ marginBottom: 12 }}>
        <Bar icon="📊" title="Par compte (plan comptable suisse)" color={C.navy} />
        <div style={{ padding: 13 }}>
          {parCat.map((c) => (
            <div
              key={c.k}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '7px 0',
                borderBottom: '1px solid ' + C.border,
              }}
            >
              <div style={{ fontSize: 9.5 }}>
                <b style={{ color: C.navy }}>{c.compte}</b> · {c.lib}
              </div>
              <div style={{ fontSize: 11, fontWeight: 800 }}>
                {DEVISE} {fmt(c.total)}
              </div>
            </div>
          ))}
        </div>
      </Card>
      {envoi && (
        <EnvoiDepenses
          choisies={fraisEnvoi.filter((x) => cochees.includes(x.id))}
          nombre={fraisEnvoi.length}
          soc={soc}
          frais={frais}
          filtre={filtreEnvoi}
          onFiltre={changeFiltreEnvoi}
          onToutCocher={() =>
            setCochees((p) =>
              fraisEnvoi.every((x) => p.includes(x.id)) ? [] : fraisEnvoi.map((x) => x.id),
            )
          }
          onFermer={() => {
            setEnvoi(false);
            setCochees([]);
          }}
        />
      )}
      {(envoi ? fraisEnvoi : frais).map((x) => {
        const c = FRAIS_CAT[x.type] || {};
        return (
          <Card key={x.id} style={{ marginBottom: 7 }}>
            <div
              style={{
                padding: '11px 13px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  cursor: envoi || aUnJustificatif(x) ? 'pointer' : 'default',
                }}
                onClick={(ev) => {
                  if (ev.target.closest('button')) return;
                  // En choisissant quoi envoyer, toucher la ligne la coche.
                  if (envoi) {
                    if (!ev.target.closest('input')) bascule(x.id);
                    return;
                  }
                  // Toucher la ligne ouvre la vraie facture, comme l'image.
                  if (aUnJustificatif(x)) ouvreJustificatif(x);
                }}
              >
                {envoi && (
                  <input
                    type="checkbox"
                    aria-label={tr('Choisir')}
                    checked={cochees.includes(x.id)}
                    onChange={() => bascule(x.id)}
                    style={{ width: 18, height: 18, flexShrink: 0 }}
                  />
                )}
                {aUnJustificatif(x) ? (
                  <button
                    type="button"
                    aria-label={tr('Voir le justificatif')}
                    onClick={() => ouvreJustificatif(x)}
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    {x.piece && (x.pieceMime || '').startsWith('image/') ? (
                      <img
                        src={x.piece}
                        alt=""
                        style={{
                          width: 30,
                          height: 30,
                          objectFit: 'cover',
                          borderRadius: 6,
                          border: '1px solid ' + C.border,
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 18 }}>📄</span>
                    )}
                  </button>
                ) : (
                  <span style={{ fontSize: 16 }}>{c.icon}</span>
                )}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700 }}>
                    {x.libelle}
                    {estUnPdf(x) && (
                      <span
                        style={{
                          marginLeft: 6,
                          background: C.redL,
                          color: C.red,
                          borderRadius: 4,
                          padding: '1px 5px',
                          fontSize: 7.5,
                          fontWeight: 800,
                          verticalAlign: 'middle',
                        }}
                      >
                        PDF
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 8, color: C.muted }}>
                    {c.icon} {c.lib} · {c.compte} · {x.date}
                    {x.paiement ? ' · ' + tr(libellePaiement(x.paiement)) : ''}
                    {x.piece ? ' · 📎' : ''}
                    {/* Notée depuis le relevé bancaire, une dépense n'a pas de
                        ticket : sa TVA se compte comme les autres, mais l'AFC
                        ne l'admet en déduction que sur quittance. La ligne le
                        dit, pour que le justificatif soit joint à temps. */}
                    {!x.piece && !x.originalId && tvaDeLaDepense(x, FRAIS_CAT, soc) > 0
                      ? ' · ' + tr('Sans justificatif')
                      : ''}
                    {moisCouverts(x) > 1
                      ? ' · ' + trf('réparti sur {0} mois', moisCouverts(x))
                      : ''}
                  </div>
                  {x.serie && dernierDeLaSerie.get(x.serie) === x && boutonArret(x)}
                  {x.payeLe && (
                    <div style={{ fontSize: 8, color: C.green, fontWeight: 700 }}>
                      ✓ {trf('Payé le {0}', x.payeLe)}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.red }}>
                  {DEVISE} {fmt(x.montant)}
                </div>
                {x.iban && (
                  <button
                    onClick={() => setPayQrId(payQrId === x.id ? null : x.id)}
                    style={{
                      background: payQrId === x.id ? C.navy : C.navyXL,
                      color: payQrId === x.id ? '#fff' : C.navy,
                      border: 'none',
                      borderRadius: 8,
                      padding: '5px 8px',
                      fontSize: 8.5,
                      fontWeight: 800,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    💳 Payer
                  </button>
                )}
                <button
                  onClick={() => {
                    // La catégorie d'une dépense enregistrée est un choix : une
                    // nouvelle photo ne la remplace pas.
                    categorieChoisie.current = true;
                    setF({ ...x, editId: x.id });
                    setAdd(true);
                    window.scrollTo?.(0, 0);
                  }}
                  aria-label="Modifier la dépense"
                  style={{
                    background: C.navyXL,
                    color: C.navy,
                    border: 'none',
                    borderRadius: 8,
                    padding: '5px 8px',
                    fontSize: 8.5,
                    fontWeight: 800,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ✎
                </button>
                <button
                  onClick={() => setA4(x)}
                  title="Justificatif A4"
                  style={{
                    background: C.tealL,
                    color: C.teal,
                    border: 'none',
                    borderRadius: 8,
                    padding: '5px 8px',
                    fontSize: 8.5,
                    fontWeight: 800,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  🖨 A4
                </button>
                <button
                  onClick={() => {
                    // Une depense effacee emporte son justificatif, que rien ne
                    // reconstitue : la question se pose avant, pas apres.
                    if (window.confirm('Supprimer cette dépense et son justificatif ?')) {
                      setFrais((p) => p.filter((y) => y.id !== x.id));
                      // Rattachée à un chantier, elle y avait aussi été inscrite
                      // comme facture fournisseur : restée là, elle comptait
                      // encore dans le coût du chantier. Elle part avec la
                      // dépense — elle seule, pas une autre du même montant.
                      if (x.chantierId && typeof setChantiers === 'function') {
                        const catLib = (FRAIS_CAT[x.type] || {}).lib || x.type;
                        setChantiers((p) =>
                          p.map((c) => {
                            if (String(c.id) !== String(x.chantierId)) return c;
                            const i = factureDeLaDepense(c.factures, x, catLib);
                            return i < 0
                              ? c
                              : { ...c, factures: c.factures.filter((_, k) => k !== i) };
                          }),
                        );
                      }
                    }
                  }}
                  aria-label="Supprimer la dépense"
                  style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>
            </div>
            {x.iban && payQrId === x.id && (
              <div style={{ padding: '0 13px 13px', textAlign: 'center' }}>
                <div
                  style={{
                    display: 'inline-block',
                    background: '#fff',
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid ' + C.border,
                  }}
                >
                  <QRCode
                    size={130}
                    color={C.ink}
                    value={swissQR({
                      iban: x.iban,
                      nom: x.libelle || 'Fournisseur',
                      adr: '',
                      npa: '',
                      amount: x.montant,
                      clientNom: (soc && soc.nom) || '',
                      clientAdr: (soc && soc.adr) || '',
                      clientNpa: (soc && soc.npa) || '',
                      refType: 'NON',
                      ref: '',
                      msg: x.libelle || 'Depense',
                    })}
                  />
                </div>
                <div style={{ fontSize: 8.5, color: C.muted, marginTop: 6 }}>
                  Scanne pour payer dans ton e-banking
                </div>
              </div>
            )}
          </Card>
        );
      })}
      <Card style={{ marginTop: 14, marginBottom: 8 }}>
        <Bar
          icon="🏭"
          title="Carnet fournisseurs"
          color={C.purple}
          sub="Tes fournisseurs et leurs coordonnées"
        />
        <div style={{ padding: 13 }}>
          {!fournForm ? (
            <div
              onClick={() => setFournForm({ nom: '', contact: '', tel: '', email: '', note: '' })}
              style={{
                textAlign: 'center',
                border: '1px dashed ' + C.purple,
                borderRadius: 9,
                padding: 10,
                fontSize: 10,
                fontWeight: 700,
                color: C.purple,
                cursor: 'pointer',
                marginBottom: fournisseurs.length ? 11 : 0,
              }}
            >
              ＋ Ajouter un fournisseur
            </div>
          ) : (
            <div
              style={{
                border: '1px solid ' + C.border,
                borderRadius: 10,
                padding: 12,
                marginBottom: 11,
              }}
            >
              <Inp
                label="Nom du fournisseur"
                value={fournForm.nom}
                onChange={(e) => setFournForm((p) => ({ ...p, nom: e.target.value }))}
                placeholder="Ex. Ceramica SA"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Inp
                  label="Personne de contact"
                  value={fournForm.contact}
                  onChange={(e) => setFournForm((p) => ({ ...p, contact: e.target.value }))}
                  placeholder="M. Rossi"
                />
                <Inp
                  label="Téléphone"
                  value={fournForm.tel}
                  onChange={(e) => setFournForm((p) => ({ ...p, tel: e.target.value }))}
                  placeholder="+41 …"
                />
              </div>
              <Inp
                label="E-mail"
                value={fournForm.email}
                onChange={(e) => setFournForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="contact@fournisseur.ch"
              />
              <Inp
                label="Note (produits, conditions…)"
                value={fournForm.note}
                onChange={(e) => setFournForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="Ex. carrelage, livraison 48h"
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Btn
                  label="✓ Enregistrer"
                  sm
                  color={C.purple}
                  full
                  disabled={!fournForm.nom.trim()}
                  onClick={() => {
                    const nf = {
                      id: fournForm.id || uid(),
                      nom: fournForm.nom.trim(),
                      contact: fournForm.contact || '',
                      tel: fournForm.tel || '',
                      email: fournForm.email || '',
                      note: fournForm.note || '',
                    };
                    setSoc &&
                      setSoc((p) => {
                        const list = p && Array.isArray(p.fournisseurs) ? p.fournisseurs : [];
                        const exists = list.some((x) => x.id === nf.id);
                        return {
                          ...p,
                          fournisseurs: exists
                            ? list.map((x) => (x.id === nf.id ? nf : x))
                            : [...list, nf],
                        };
                      });
                    setFournForm(null);
                  }}
                />
                <Btn label="Annuler" sm ghost color={C.grey} onClick={() => setFournForm(null)} />
              </div>
            </div>
          )}
          {fournisseurs.map((fr) => (
            <div
              key={fr.id}
              style={{
                border: '1px solid ' + C.border,
                borderRadius: 9,
                padding: '10px 12px',
                marginBottom: 7,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800 }}>{fr.nom}</div>
                  {fr.contact && <div style={{ fontSize: 9, color: C.muted }}>{fr.contact}</div>}
                  {fr.note && (
                    <div style={{ fontSize: 8.5, color: C.grey, marginTop: 2 }}>{fr.note}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  <Btn label="✎" sm ghost color={C.navy} onClick={() => setFournForm({ ...fr })} />
                  <Btn
                    label="✕"
                    sm
                    ghost
                    color={C.red}
                    onClick={() => {
                      // Du carnet des bons d'achat : il en sort pour les deux.
                      if (fr.duCarnet) {
                        const id = Number(String(fr.id).replace('bon-', ''));
                        bonsAchatApi
                          .supprimeFournisseur(id)
                          .then(() => setCarnetBons((l) => l.filter((b) => b.id !== id)))
                          .catch(() => {});
                        return;
                      }
                      setSoc &&
                        setSoc((p) => ({
                          ...p,
                          fournisseurs: (p.fournisseurs || []).filter((x) => x.id !== fr.id),
                        }));
                    }}
                  />
                </div>
              </div>
              {(fr.tel || fr.email) && (
                <div style={{ display: 'flex', gap: 7, marginTop: 8 }}>
                  {fr.tel && (
                    <a
                      href={'tel:' + fr.tel.replace(/\s/g, '')}
                      style={{ textDecoration: 'none', flex: 1 }}
                    >
                      <div
                        style={{
                          background: C.navyXL,
                          color: C.navy,
                          borderRadius: 8,
                          padding: '7px',
                          textAlign: 'center',
                          fontSize: 9,
                          fontWeight: 700,
                        }}
                      >
                        📞 Appeler
                      </div>
                    </a>
                  )}
                  {fr.email && (
                    <a href={'mailto:' + fr.email} style={{ textDecoration: 'none', flex: 1 }}>
                      <div
                        style={{
                          background: C.navyXL,
                          color: C.navy,
                          borderRadius: 8,
                          padding: '7px',
                          textAlign: 'center',
                          fontSize: 9,
                          fontWeight: 700,
                        }}
                      >
                        ✉ E-mail
                      </div>
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
          {fournisseurs.length === 0 && !fournForm && (
            <div style={{ fontSize: 9, color: C.muted, textAlign: 'center', marginTop: 10 }}>
              Aucun fournisseur enregistré. Ajoute-les pour les retrouver ici.
            </div>
          )}
        </div>
      </Card>
    </Shell>
  );
}
